import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import { initI18n } from "../../../packages/i18n/src/config";
import App from "./App";
import "../../../index.css";
import { logout } from "./store/auth";
import {
  authenticateUser,
  handleAuthenticationError,
} from "@surgia/api/src";
import { getTenant } from "@surgia/api/src/admin/tenant";
import { Toaster } from "sonner";
import { getKeycloakUser, getKeycloakTokens } from "./utils/keyclock";
import { getStorefrontCurrencyLookup } from "@surgia/api/src/shared/lookups";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const i18n = initI18n();

const oidcConfig = {
  authority: import.meta.env.VITE_KEYCLOAK_ISSUER,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,

  // Key configurations for cross-tab authentication
  // automaticSilentRenew: true,
  silent_redirect_uri:
    import.meta.env.VITE_SILENT_SSO_URL ||
    `${window.location.origin}/silent-sso.html`,
  includeIdTokenInSilentRenew: true,
  response_type: "code",
  scope: "openid profile email",
  // MODIFY THESE SETTINGS:
  loadUserInfo: false, // Don't load user info on startup - faster initialization
  monitorSession: true,
  checkSessionInterval: 2000, // Increase back to 2 seconds - 500ms is too aggressive

  // ADD THESE NEW SETTINGS:
  revokeTokensOnSignout: true, // Revoke tokens when signing out
  automaticSilentRenew: false,

  onSigninCallback: async () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    const keycloakUser = getKeycloakUser();
    if (keycloakUser?.access_token)
      localStorage.setItem("token", keycloakUser?.access_token || "");

    // ... rest of your existing onSigninCallback logic ...
    const invitationToken = sessionStorage.getItem("invitation_token");

    if (invitationToken) {
      sessionStorage.removeItem("invitation_token");
    }

    const keycloakTokens = getKeycloakTokens();

    // Call our sign-in API with HUB component
    try {
      const authResult = await authenticateUser(keycloakTokens);
      if (authResult.errorMessageKey == "Access denied") {
        store.dispatch(logout());
        localStorage.removeItem("token");
        sessionStorage.clear();
        window.location.href = `/wrong-portal`;
        return;
      } else if (!authResult?.hasAccess) {
        handleAuthenticationError(store, logout);
        const keycloakLogoutUrl = `${import.meta.env.VITE_KEYCLOAK_ISSUER}/protocol/openid-connect/logout?client_id=${import.meta.env.VITE_KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${window.location.origin}`;
        window.location.href = keycloakLogoutUrl;
        return;
      }

      // Note: localStorage updates (permissions, tenant_id, user_groups, branches) 
      // are now handled inside authenticateUser to prevent race conditions

      // Get tenant_id from auth result
      const tenant_id = authResult?.userInfo?.user_profile?.tenant?.id;

      // Fetch tenant details to get storefront_id
      let storefront_id: number | undefined;
      if (tenant_id) {
        try {
          const tenantResponse = await getTenant({
            tenant_id: tenant_id, // Changed from id to tenant_id
            status: "ACTIVE",
            component_id: Number(import.meta.env.VITE_PUBLIC_COMPONENT_ID),
          });
          storefront_id = tenantResponse?.data?.[0]?.storefront_id;
        } catch (error) {
          console.error("Error fetching tenant details:", error);
          // Continue without storefront_id if fetch fails
        }
      }

      // Fetch currency after getting storefront_id
      let currency_code: string | undefined;
      let exchange_rate: number | undefined;
      if (storefront_id) {
        try {
          const currencyResponse = await getStorefrontCurrencyLookup({
            storefront_id: Number(storefront_id),
            is_base_currency: true,
          });
          const currencyInfo = currencyResponse?.[0];
          currency_code = currencyInfo?.currency?.code;
          exchange_rate = currencyInfo?.exchange_rate;
        } catch (error) {
          console.error("Error fetching currency:", error);
          // Continue with defaults if fetch fails
        }
      }

      const processedLastAccessedBranch = (() => {
        const lastAccessedBranch =
          authResult?.userInfo?.user_info?.last_accessed_branch;

        if (lastAccessedBranch === "allBranches") {
          const userBranches =
            authResult?.userInfo?.user_info?.user_branches || [];
          return userBranches.map((branch: any) => branch.id).join(",");
        }
        return lastAccessedBranch;
      })();

      localStorage.setItem(
        "lastAccessedBranch",
        JSON.stringify(processedLastAccessedBranch)
      );

      const authDataPayload = {
        ...keycloakTokens,
        userInfo: {
          email: authResult.userInfo?.user_info?.email,
          first_name: authResult.userInfo?.user_info?.first_name,
          last_name: authResult.userInfo?.user_info?.last_name,
          full_name: authResult.userInfo?.user_info?.full_name,
          mobile: authResult.userInfo?.user_info?.mobile,
          subject_id: authResult.userInfo?.user_info?.subject_id,
          creation_date: authResult.userInfo?.user_info?.creation_date,
          last_accessed_branch_id: processedLastAccessedBranch,
          my_profile: authResult.userInfo?.user_info?.my_profile,
          user_groups: authResult.userInfo?.user_info?.user_groups,
          last_login: authResult.userInfo?.user_info?.last_login,
          user_permissions: authResult.userInfo?.user_permissions,
          user_branches: authResult.userInfo?.user_info?.user_branches,
        },
        isAuthenticated: true,
        tenant_id: tenant_id,
        storefront_id: storefront_id ?? null, // Convert undefined to null
        currency_code: currency_code ?? null, // Convert undefined to null
        exchange_rate: exchange_rate ?? null, // Convert undefined to null
      };

      sessionStorage.setItem(
        "pendingAuthData",
        JSON.stringify(authDataPayload)
      );

      // Note: updateLastLoginTime() removed due to CORS issues
      // The backend should track last login via the sign-in endpoint instead

      const storedPath = sessionStorage.getItem("redirectPath");
      if (storedPath) {
        sessionStorage.removeItem("redirectPath");
        window.location.href = storedPath;
      } else {
        window.location.href = "/";
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
    }
  },

  onSignoutCallback: () => {
    store.dispatch(logout());
    localStorage.clear();
    sessionStorage.clear();
  },
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider {...oidcConfig}>
              <App />
              <Toaster position="top-right" richColors={true} duration={1500} />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </I18nextProvider>
    </PersistGate>
  </Provider>
);
